import { useCallback, useContext, useEffect, useMemo } from "react";
import { Box, Skeleton } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import NonIdealNew from "../../../../common/NonIdealNew";
import { ParagraphText } from "../../../../../Libraries";
import ReportPageHeader from "../ReportPageHeader";
import { FilterNonIdeal } from "../../../../common";
import styles from "../styles.module.css";
import { LanguageContext } from "../../../../../context/LanguageContext";
import { useReports } from "../../../../../hooks/useReports";
import { useAuthContext } from "../../../../../context/AuthContext";
import {
  formatReportCurrency,
  REPORT_DATE_FILTERS,
} from "../../../../../utils";
import dayjs from "dayjs";
import useReportExport from "../../../../../hooks/useReportExport";
import useReportFilter from "../../../../../hooks/useReportFilter";
import { analytics, triggerLogEvent } from "../../../../../config/firebase";

const SKELETON_ROWS = [
  { labelWidth: 110, valueWidth: 88 },
  { labelWidth: 150, valueWidth: 48 },
  { labelWidth: 200, valueWidth: 96 },
];

const DiscountSummary = () => {
  const [I18n] = useContext(LanguageContext);
  const { business } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();

  const { handleFetchReport, loading, discountSummary } = useReports(I18n);
  const { handleExport, exportLoading } = useReportExport("discount_summary");

  useEffect(() => {
    triggerLogEvent(analytics, "report_discount_summary_visited", {});
  }, []);

  const fetchReport = useCallback(
    (start, end) => {
      handleFetchReport({
        business_id: business?.id,
        report_type: "discount_summary",
        start_date: dayjs(start?.toDate ? start.toDate() : start).toISOString(),
        end_date: dayjs(end?.toDate ? end.toDate() : end).toISOString(),
      });
    },
    [handleFetchReport, business?.id],
  );

  const {
    headerDefaultFilter,
    isYearlyFilter,
    resetCount,
    handleFilterDate,
    handleResetFilter,
  } = useReportFilter(fetchReport, "discount_summary");

  const rows = useMemo(
    () => [
      {
        labelKey: "total_discount",
        value: formatReportCurrency(discountSummary.total_discount),
      },
      {
        labelKey: "orders_with_discount",
        value: discountSummary.orders_with_discounts ?? "—",
      },
      {
        labelKey: "average_discount_per_order",
        value: formatReportCurrency(discountSummary.average_discount_per_order),
      },
    ],
    [discountSummary],
  );

  const hasNoData =
    !discountSummary.total_discount &&
    !discountSummary.orders_with_discounts &&
    !discountSummary.average_discount_per_order;

  const showNonIdeal = isYearlyFilter && !loading && hasNoData;

  const minDate = useMemo(() => dayjs().subtract(1, "year"), []);
  const maxDate = useMemo(() => dayjs().endOf("day"), []);

  return (
    <Box id="discount_summary_page_wrapper" className={styles.pageWrapper}>
      <ReportPageHeader
        title={I18n.discount_summary}
        id="discount_summary"
        dateFilters={REPORT_DATE_FILTERS}
        onFilterDate={handleFilterDate}
        minDate={minDate}
        maxDate={maxDate}
        defaultFilter={headerDefaultFilter}
        resetKey={resetCount}
        hideActions={showNonIdeal}
        onExport={handleExport}
        exportLoading={exportLoading}
      />

      {showNonIdeal ? (
        <Box id="discount_summary_nonideal_body" className={styles.pageBody}>
          <NonIdealNew
            buttonOneId="discount_summary_nonideal_start_sale_btn"
            title={I18n.no_data_yet}
            subtitle={I18n.it_looks_like_you_dont_have_any_data_yet}
            button1Title={I18n.start_new_sale}
            onButton1Click={() => {
              triggerLogEvent(analytics, "report_nonideal_start_new_sale", {
                report_type: "discount_summary",
              });
              navigate("/QuickSale/QuickSaleForm", {
                state: { pathname: location.pathname },
              });
            }}
            loading={false}
            disabledOne={false}
          />
        </Box>
      ) : (
        <Box id="discount_summary_page_body" className={styles.pageBody}>
          {loading ? (
            SKELETON_ROWS.map(({ labelWidth, valueWidth }, i) => (
              <Box key={i} className={styles.summaryRow}>
                <Skeleton
                  variant="rounded"
                  animation="wave"
                  width={labelWidth}
                  height={18}
                  sx={{ borderRadius: "6px" }}
                />
                <Skeleton
                  variant="rounded"
                  animation="wave"
                  width={valueWidth}
                  height={18}
                  sx={{ borderRadius: "6px" }}
                />
              </Box>
            ))
          ) : hasNoData ? (
            <FilterNonIdeal
              title={I18n.no_transactions_matching_those_filters}
              subTitle={`${I18n.try_adjusting_filters} `}
              subTitleText={I18n.view_yearly_data}
              subTitleTextOnClick={handleResetFilter}
              subTitleTextId="discount_summary_filter_view_yearly"
            />
          ) : (
            rows.map(({ labelKey, value }) => (
              <Box
                key={labelKey}
                id={`discount_summary_row_${labelKey}`}
                className={styles.summaryRow}
              >
                <ParagraphText
                  id={`discount_summary_label_${labelKey}`}
                  className={styles.summaryLabel}
                >
                  {I18n[labelKey]}
                </ParagraphText>
                <ParagraphText
                  id={`discount_summary_value_${labelKey}`}
                  className={styles.summaryValue}
                >
                  {value}
                </ParagraphText>
              </Box>
            ))
          )}
        </Box>
      )}
    </Box>
  );
};

export default DiscountSummary;
